<template>
  <div class="dashboards-map" :class="{ 'dashboards-map_loading': isLoading }" v-click-outside="closePopupInfo">
    <div id="map-dashboard" class="dashboards-map__map"></div>
    <Transition>
      <MapPopup v-if="openPopupInfo" mod="regions" @closePopup="closePopupInfo">
        <template v-slot:header>
          <p class="dashboards-map__popup-name">{{ infoPopup.name }}</p>
        </template>
        <template v-slot:body>
          <p class="dashboards-map__popup-year">{{ year }} год</p>
          <p class="dashboards-map__popup-rating">{{ infoPopup.position }} место в рейтинге эффективности</p>
          <ButtonComponent
            @click="toRegionPage(infoPopup.id)"
            class="button-redesign--commission-form"
            mod="gradient-bg"
            >Подробнее</ButtonComponent
          >
        </template>
      </MapPopup>
    </Transition>
    <MapScaleQuantity name="Индекс активности субъектов" :squareBar="squareBar" />
  </div>
</template>

<script>
  import { defineComponent } from 'vue';
  import L from 'leaflet';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import MapScaleQuantity from '@/common/components/redesigned/MapScaleQuantity.vue';
  import MapPopup from '@/common/components/redesigned/popups/MapPopup.vue';
  import regions from '@/common/region.json';

  import { MAP_REGION_SCALE } from '../../constants/constants.js';
  import { getMapRegionScaleColor, getRatingDisplayValue } from '../../utils/rating';

  export default defineComponent({
    name: 'DashboardsMap',
    components: {
      MapScaleQuantity,
      ButtonComponent,
      MapPopup,
    },
    props: {
      listRegionEffective: {
        type: Array,
        default: () => [],
      },
      year: {
        type: String,
        required: true,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        regions,
        map: null,
        center: [66, 100],
        geoJsonLayer: null,
        squareBar: MAP_REGION_SCALE,
        openPopupInfo: false,
        infoPopup: {
          id: '',
          name: '',
          rating: 0,
          position: null,
        },
      };
    },
    mounted() {
      this.onDrawingMap();
    },
    watch: {
      listRegionEffective: {
        handler() {
          this.updateMapData();
        },
        deep: true,
      },
    },
    methods: {
      onDrawingMap() {
        const mapContainer = window.document.getElementById('map-dashboard');
        while (mapContainer?.firstChild) {
          mapContainer.removeChild(mapContainer.firstChild);
        }
        if (this.map) {
          this.map.remove();
          this.map = null;
        }

        this.map = L.map('map-dashboard', {
          center: this.center,
          zoom: 2.7,
          zoomSnap: 0.1,
        });

        L.tileLayer('', {
          maxZoom: 5,
          minZoom: 2.7,
        }).addTo(this.map);

        this.geoJsonLayer = L.geoJSON(this.regions, {
          weight: 0.5,
          fillOpacity: 1,
        }).addTo(this.map);

        this.restyleGeoJsonLayer();
      },

      updateMapData() {
        if (this.map && this.geoJsonLayer) {
          this.restyleGeoJsonLayer();
        }
      },

      onEachFeature(feature, layer) {
        const self = this;
        layer.on('mouseup', () => {
          const index = self.listRegionEffective.findIndex((i) => +i.regionCodes.includes(+feature.properties.code));
          if (index != -1) {
            this.infoPopup.id = this.listRegionEffective[index].regionId;
            this.infoPopup.name = this.listRegionEffective[index].regionName;
            this.infoPopup.rating = getRatingDisplayValue(this.listRegionEffective[index].activityRatingIndex);
            this.infoPopup.position = this.listRegionEffective[index].place;
            this.openPopupInfo = true;
          }
        });
        layer.on('mouseover', function () {
          this.unbindTooltip();
          const index = self.listRegionEffective.findIndex((i) => +i.regionCodes.includes(+feature.properties.code));
          if (index != -1) {
            const name = self.listRegionEffective[index].regionName;
            this.bindTooltip(name).openTooltip();
          }
        });
        layer.on('mouseout', function () {
          this.unbindTooltip();
        });
      },

      styleMap(feature) {
        const regionCodes = feature.properties.code;
        const region = this.listRegionEffective.find((i) => +i.regionCodes.includes(regionCodes));
        const color = getMapRegionScaleColor(region?.activityRatingIndex, this.squareBar);

        return { color: '#319CFF', fillColor: color, weight: 1, opacity: 1 };
      },

      restyleGeoJsonLayer() {
        if (this.geoJsonLayer) {
          this.geoJsonLayer.eachLayer(this.restyleLayer);
        }
      },

      restyleLayer(layer) {
        const style = this.styleMap(layer.feature);
        layer.setStyle(style);
        this.onEachFeature(layer.feature, layer);
      },

      toRegionPage(id) {
        this.$router.push({
          name: 'RegionDashboardsVES',
          params: { regionRfId: id },
          query: { year: this.year },
        });
      },

      closePopupInfo() {
        this.openPopupInfo = false;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .dashboards-map {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: relative;

    &_loading {
      opacity: 0.7;
    }

    &__map {
      width: 100%;
      height: 100%;
      background: #fff;
      :deep(.leaflet-tooltip) {
        box-shadow: none;
        font: $fira-14;

        &::before {
          content: none;
        }
      }

      :deep(.leaflet-interactive) {
        outline: none;
        transition: all 0.3s ease-in-out;
      }
    }

    &__popup {
      &-name {
        font: $fira-14-20-B;
      }
      &-year {
        font: $fira-12;
        margin-bottom: 8px;
      }
      &-rating {
        font: $fira-12-B;
        margin-bottom: 16px;
      }
    }
  }

  :deep(.leaflet-top),
  :deep(.leaflet-bottom),
  :deep(.leaflet-control),
  :deep(.leaflet-pane) {
    z-index: 99;
  }
</style>
